import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { faSquareDashed } from '@fortawesome/pro-solid-svg-icons';
import { ReactComponent as EquipmentFinancing } from '../../shared/icons/EquipmentFinancing.svg';
import { ReactComponent as GrowthCapital } from '../../shared/icons/GrowthCapital.svg';
import { ReactComponent as Refinance } from '../../shared/icons/Refinance.svg';
import { ReactComponent as NewBusiness } from '../../shared/icons/NewBusiness.svg';
import { ReactComponent as OtherFinancingNeeds } from '../../shared/icons/OtherFinancingNeeds.svg';
import { ReactComponent as OwnerOccupiedRealEstate } from '../../shared/icons/OwnerOccupiedRealEstate.svg';
import { ReactComponent as WorkingCapital } from '../../shared/icons/WorkingCapital.svg';
import { Grid, Skeleton, useTheme } from '@mui/material';
import NumberFormat from 'react-number-format';
import { Link, useParams } from 'react-router-dom';
import { CerebroHeading, CerebroLink, CerebroText, GetCollateralTypesForDetails } from '.';
import {
	useDealFinancialMetricsQuery,
	useDealQuery,
	useDealRequestAllocationQuery,
	useDealRequestAllocationRealEstatesQuery,
	useDealRequiredDocumentsQuery,
	useDebtScheduleQuery,
} from '../hooks';
import {
	Deal,
	DealFinancialMetric,
	dealPreferences,
	dealPreferencesSelected,
	DealRequestAllocation,
	isBalanceSheetMetricType,
} from '../models';
import React from 'react';
import { IncomeStatementMetricTypes } from './IncomeStatementRow';
import { Urls } from '../context';

export const CerebroSummary: React.FC<CerebroSummaryProps> = ({ showLinks, compressView }) => {
	const { guid } = useParams<'guid'>();
	const theme = useTheme();
	const dealQuery = useDealQuery(guid);
	const allocationQuery = useDealRequestAllocationQuery(guid);
	const realEstatesQuery = useDealRequestAllocationRealEstatesQuery(guid);
	const debtScheduleQuery = useDebtScheduleQuery(guid);
	const documentsQuery = useDealRequiredDocumentsQuery(guid);
	const dealFinancialQuery = useDealFinancialMetricsQuery(guid, 'Borrower', IncomeStatementMetricTypes);
	const collateralTypes = GetCollateralTypesForDetails(allocationQuery.data, 'Borrower');
	const dealCollateralQuery = useDealFinancialMetricsQuery(guid, 'Borrower', collateralTypes);
	const dealFinancialAcquisitionQuery = useDealFinancialMetricsQuery(guid, 'Acquisition', IncomeStatementMetricTypes);
	const acquisitionCollateralTypes = GetCollateralTypesForDetails(allocationQuery.data, 'Borrower');
	const dealAcquisitionCollateralQuery = useDealFinancialMetricsQuery(
		guid,
		'Acquisition',
		acquisitionCollateralTypes
	);

	const equipmentTotal =
		(allocationQuery.data?.purchaseEquipmentAmount ?? 0) +
		(allocationQuery.data?.purchaseVehiclesAmount ?? 0) +
		(allocationQuery.data?.purchaseMarineAmount ?? 0) +
		(allocationQuery.data?.purchaseHelicopterAmount ?? 0) +
		(allocationQuery.data?.purchaseFixedWingAircraftAmount ?? 0);

	const realEstateTotal =
		realEstatesQuery.data?.reduce(
			(sum, realEstate) => (realEstate.termLoanNeeds ?? 0) + (realEstate.revolvingDebtNeeds ?? 0) + sum,
			0
		) ?? 0;

	const refinanceTotal =
		debtScheduleQuery.data
			?.filter(i => i.isRefinance)
			.reduce((sum, schedule) => sum + (schedule.presentBalance ?? 0), 0) ?? 0;
	const existingDebtTotal =
		debtScheduleQuery.data
			?.filter(i => !i.isRefinance)
			.reduce((sum, schedule) => sum + (schedule.presentBalance ?? 0), 0) ?? 0;

	const workingCapitalTotal = allocationQuery.data?.totalWorkingCapitalAmount ?? 0;

	const growthCapitalTotal =
		(allocationQuery.data?.totalGrowthCapitalHardCostAmount ?? 0) +
		(allocationQuery.data?.totalGrowthCapitalSoftCostAmount ?? 0);

	const otherFinancingTotal = allocationQuery.data?.otherFinancingAmount ?? 0;

	const isAcquisition = allocationQuery.data?.acquiringBusiness === true;

	const isStandaloneAcquisition = isAcquisition && allocationQuery.data?.dealType === 'Acquisitions';
	const isAddOnAcquisition = isAcquisition && allocationQuery.data?.dealType === 'AcquisitionAddon';
	const isBuyOutAcquisition =
		isAcquisition &&
		(allocationQuery.data?.dealType === 'AcquisitionManagementBuyout' ||
			allocationQuery.data?.dealType === 'AcquisitionPartnerBuyout');

	const showBorrower = !isAcquisition || isAddOnAcquisition || isStandaloneAcquisition;
	const showAcquisition = isBuyOutAcquisition || isAddOnAcquisition;

	const requestsComplete = isRequestComplete(allocationQuery.data, realEstateTotal, refinanceTotal);
	const companyComplete = showBorrower ? isCompanySectionComplete(dealQuery.data, true) : false;
	const companyAcquisitionComplete = showAcquisition ? isCompanySectionComplete(dealQuery.data, false) : false;

	const financialComplete = showBorrower
		? isFinancialSectionComplete(dealFinancialQuery.data, dealCollateralQuery.data)
		: false;
	const financialAcquisitionComplete = showAcquisition
		? isFinancialSectionComplete(dealFinancialAcquisitionQuery.data, dealAcquisitionCollateralQuery.data)
		: false;

	const documentsCount = documentsQuery.data?.length ?? 0;
	const documentsDone = documentsQuery.data?.filter(i => i.notApplicable || i.completedOn).length ?? 0;
	const documentsComplete = documentsCount === 0 || documentsCount === documentsDone;

	const typesPreferred = dealPreferences(allocationQuery.data);
	const typesSelected = dealPreferencesSelected(allocationQuery.data);

	const loanOptionsComplete = typesPreferred.length > 0 || typesSelected.length > 0;

	const getCompleteIndicator = (isComplete: boolean): JSX.Element => {
		return isComplete ? (
			<FontAwesomeIcon
				icon={faCircleCheck}
				style={{ color: theme.palette.success.main, paddingRight: 6 }}
				size="sm"
			/>
		) : (
			<FontAwesomeIcon icon={faSquareDashed} style={{ paddingRight: 6 }} size="sm" />
		);
	};

	return (
		<>
			{allocationQuery.isLoading ||
				documentsQuery.isLoading ||
				dealFinancialQuery.isLoading ||
				dealCollateralQuery.isLoading ||
				dealFinancialAcquisitionQuery.isLoading ||
				dealAcquisitionCollateralQuery.isLoading ||
				(dealQuery.isLoading && <Skeleton height={400} />)}

			{allocationQuery.isSuccess &&
				dealFinancialQuery.isSuccess &&
				dealCollateralQuery.isSuccess &&
				dealFinancialAcquisitionQuery.isSuccess &&
				dealAcquisitionCollateralQuery.isSuccess &&
				documentsQuery.isSuccess &&
				dealQuery.isSuccess && (
					<>
						<CerebroHeading size="h2" component="h2" sx={{ marginBottom: compressView ? 1.5 : 3 }}>
							SUMMARY
						</CerebroHeading>

						<CerebroText sx={{ marginBottom: compressView ? 1.5 : 3 }}>
							Click each section heading to update the information.
						</CerebroText>

						<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
							<Grid item xs={12}>
								{showLinks ? (
									<CerebroLink
										component={Link}
										to={`/${guid}/${Urls.Deal.Introduction}`}
										style={{
											display: 'flex',
											color: theme.palette.text.primary,
											alignItems: 'center',
											marginBottom: 3,
											fontWeight: 'bold',
										}}
									>
										{getCompleteIndicator(requestsComplete)}
										Loan Request(s)
									</CerebroLink>
								) : (
									<CerebroText sx={{ fontWeight: 'bold' }}>Loan Request(s)</CerebroText>
								)}
							</Grid>
							{!requestsComplete && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={11}>
										You have not completed the loan request section.
									</Grid>
								</>
							)}
							{allocationQuery.data.totalAcquisitionCapitalNeededAmount > 0 &&
								allocationQuery.data.acquiringBusiness === true && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>
												<NewBusiness style={{ width: '24', height: '24' }} />
												Acquiring a Business
											</CerebroText>
										</Grid>
										<Grid item xs={4} md={7} alignSelf="flex-end">
											<CerebroText variant="small">
												<NumberFormat
													value={allocationQuery.data.valueOfferedAmount ?? 0}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Offered Value) - '}
												<br />
												<NumberFormat
													value={allocationQuery.data.sellerEquityRollAmount}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Equity Roll) - '}
												<br />
												<NumberFormat
													value={allocationQuery.data.earnoutAmount ?? 0}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Earnout) - '}
												<br />
												<NumberFormat
													value={allocationQuery.data.buyerCashAmount ?? 0}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Cash) - '}
												<br />
												{allocationQuery.data.workingCapitalForAcquisition == true && (
													<>
														<NumberFormat
															value={
																allocationQuery.data
																	.workingCapitalForAcquisitionAmount ?? 0
															}
															displayType={'text'}
															thousandSeparator={true}
															prefix={'$'}
														/>
														{' (From Working Capital Line) - '}
														<br />
													</>
												)}
												<NumberFormat
													value={allocationQuery.data.sellerFinancingAmount ?? 0}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Seller Financing) = '}
												<br />
												<NumberFormat
													value={allocationQuery.data.totalAcquisitionCapitalNeededAmount}
													displayType={'text'}
													thousandSeparator={true}
													prefix={'$'}
												/>
												{' (Total Request Amount)'}
											</CerebroText>
										</Grid>
									</>
								)}
							{equipmentTotal > 0 && allocationQuery.data.purchaseEquipment === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<EquipmentFinancing style={{ width: '24', height: '24' }} />
											Equipment
										</CerebroText>
									</Grid>
									<Grid item xs={7} alignSelf="flex-end">
										<NumberFormat
											value={equipmentTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
							{growthCapitalTotal > 0 && allocationQuery.data.financeExpensesOrGrowth === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<GrowthCapital style={{ width: '24', height: '24' }} />
											Growth Capital
										</CerebroText>
									</Grid>
									<Grid item xs={4} md={7} alignSelf="flex-end">
										<NumberFormat
											value={growthCapitalTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
							{otherFinancingTotal > 0 && allocationQuery.data.otherFinancingNeeds === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<OtherFinancingNeeds style={{ width: '24', height: '24' }} />
											Other Financing Needs
										</CerebroText>
									</Grid>
									<Grid item xs={4} md={7} alignSelf="flex-end">
										<NumberFormat
											value={otherFinancingTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
							{realEstateTotal > 0 && allocationQuery.data.purchaseRealEstate === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<OwnerOccupiedRealEstate style={{ width: '24', height: '24' }} />
											Owner-Occupied Real Estate
										</CerebroText>
									</Grid>
									<Grid item xs={4} md={7} alignSelf="flex-end">
										<NumberFormat
											value={realEstateTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
							{refinanceTotal > 0 && allocationQuery.data.refinanceExistingDebt === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<Refinance style={{ width: '24', height: '24' }} />
											Refinance Existing Debt
										</CerebroText>
									</Grid>
									<Grid item xs={4} md={7} alignSelf="flex-end">
										<NumberFormat
											value={refinanceTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
							{workingCapitalTotal > 0 && allocationQuery.data.purchaseWorkingCapital === true && (
								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>
											<WorkingCapital style={{ width: '24', height: '24' }} />
											Working Capital
										</CerebroText>
									</Grid>
									<Grid item xs={4} md={7} alignSelf="flex-end">
										<NumberFormat
											value={workingCapitalTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>
							)}
						</Grid>

						{showBorrower && (
							<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
								<Grid item xs={12}>
									{showLinks ? (
										<CerebroLink
											component={Link}
											to={`/${guid}/${Urls.Company.Borrower}`}
											style={{
												display: 'flex',
												color: theme.palette.text.primary,
												alignItems: 'center',
												marginBottom: 3,
												fontWeight: 'bold',
											}}
										>
											{getCompleteIndicator(companyComplete)}
											Company
										</CerebroLink>
									) : (
										<CerebroText sx={{ fontWeight: 'bold' }}>Company</CerebroText>
									)}
								</Grid>
								{!companyComplete && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={11}>
											You have not completed the company section.
										</Grid>
									</>
								)}
								{dealQuery.data.companyName && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>Company Name</CerebroText>
										</Grid>
										<Grid item xs={4} md={7} alignSelf="flex-end">
											{dealQuery.data.companyName}
										</Grid>
									</>
								)}
								{dealQuery.data.companyIndustry && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>Industry</CerebroText>
										</Grid>
										<Grid item xs={4} md={7}>
											{dealQuery.data.companyIndustry.name}
										</Grid>
									</>
								)}
								{dealQuery.data.state && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>State</CerebroText>
										</Grid>
										<Grid item xs={4} md={7}>
											{dealQuery.data.state.name}
										</Grid>
									</>
								)}
							</Grid>
						)}

						{showAcquisition && (
							<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
								<Grid item xs={12}>
									{showLinks ? (
										<CerebroLink
											component={Link}
											to={`/${guid}/${Urls.Company.Acquisition}`}
											style={{
												display: 'flex',
												color: theme.palette.text.primary,
												alignItems: 'center',
												marginBottom: 3,
												fontWeight: 'bold',
											}}
										>
											{getCompleteIndicator(companyAcquisitionComplete)}
											Company - Acquisition Target
										</CerebroLink>
									) : (
										<CerebroText sx={{ fontWeight: 'bold' }}>
											Company - Acquisition Target
										</CerebroText>
									)}
								</Grid>
								{!companyAcquisitionComplete && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={11}>
											You have not completed the company acquisition target section.
										</Grid>
									</>
								)}
								{dealQuery.data.companyName && !showBorrower && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>Company Name</CerebroText>
										</Grid>
										<Grid item xs={4} md={7}>
											{dealQuery.data.companyName}
										</Grid>
									</>
								)}
								{dealQuery.data.acquisitionIndustry && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>Industry</CerebroText>
										</Grid>
										<Grid item xs={4} md={7}>
											{dealQuery.data.acquisitionIndustry.name}
										</Grid>
									</>
								)}
								{dealQuery.data.acquisitionState && (
									<>
										<Grid item xs={1}></Grid>
										<Grid item xs={7} md={4}>
											<CerebroText>State</CerebroText>
										</Grid>
										<Grid item xs={4} md={7}>
											{dealQuery.data.acquisitionState.name}
										</Grid>
									</>
								)}
							</Grid>
						)}

						{showBorrower && (
							<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
								<Grid item xs={12}>
									{showLinks ? (
										<CerebroLink
											component={Link}
											to={`/${guid}/${Urls.Financials.Borrower.BasicInfo}`}
											style={{
												display: 'flex',
												color: theme.palette.text.primary,
												alignItems: 'center',
												marginBottom: 3,
												fontWeight: 'bold',
											}}
										>
											{getCompleteIndicator(financialComplete)}
											Financials
										</CerebroLink>
									) : (
										<CerebroText sx={{ fontWeight: 'bold' }}>Financials</CerebroText>
									)}
								</Grid>

								{dealFinancialQuery.data
									.filter(i => isMetricIncomeDisplayType(i))
									.map(metric => (
										<React.Fragment key={metric.guid}>
											<Grid item xs={1}></Grid>
											<Grid item xs={7} md={4}>
												<CerebroText>{metric.metricName}</CerebroText>
											</Grid>
											<Grid item xs={4} md={7}>
												<NumberFormat
													value={metric.trailingTwelveMonths}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											</Grid>
										</React.Fragment>
									))}

								<>
									<Grid item xs={1}></Grid>
									<Grid item xs={7} md={4}>
										<CerebroText>Existing Debt</CerebroText>
									</Grid>
									<Grid item xs={4} md={7}>
										<NumberFormat
											value={existingDebtTotal}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</Grid>
								</>

								<Grid item xs={12} sx={{ marginBottom: 1 }}></Grid>

								{dealCollateralQuery.data
									.filter(
										i =>
											isBalanceSheetMetricType(i) &&
											collateralTypes.includes(i.financialMetricType ?? 'NetIncome') == true
									)
									.map(metric => (
										<React.Fragment key={metric.guid}>
											<Grid item xs={1}></Grid>
											<Grid item xs={7} md={4}>
												<CerebroText>{metric.metricName}</CerebroText>
											</Grid>
											<Grid item xs={4} md={7}>
												<NumberFormat
													value={metric.trailingTwelveMonths}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											</Grid>
										</React.Fragment>
									))}
							</Grid>
						)}

						{showAcquisition && (
							<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
								<Grid item xs={12}>
									{showLinks ? (
										<CerebroLink
											component={Link}
											to={`/${guid}/${Urls.Financials.Acquisition.Root}`}
											style={{
												display: 'flex',
												color: theme.palette.text.primary,
												alignItems: 'center',
												marginBottom: 3,
												fontWeight: 'bold',
											}}
										>
											{getCompleteIndicator(financialAcquisitionComplete)}
											Financials - Acquisition Target
										</CerebroLink>
									) : (
										<CerebroText sx={{ fontWeight: 'bold' }}>
											Financials - Acquisition Target
										</CerebroText>
									)}
								</Grid>

								{dealFinancialAcquisitionQuery.data
									.filter(i => isMetricIncomeDisplayType(i))
									.map(metric => (
										<React.Fragment key={metric.guid}>
											<Grid item xs={1}></Grid>
											<Grid item xs={7} md={4}>
												<CerebroText>{metric.metricName}</CerebroText>
											</Grid>
											<Grid item xs={4} md={7}>
												<NumberFormat
													value={metric.trailingTwelveMonths}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											</Grid>
										</React.Fragment>
									))}

								<Grid item xs={12} sx={{ marginBottom: 1 }}></Grid>

								{dealAcquisitionCollateralQuery.data
									.filter(
										i =>
											isBalanceSheetMetricType(i) &&
											acquisitionCollateralTypes.includes(i.financialMetricType ?? 'NetIncome') ==
												true
									)
									.map(metric => (
										<React.Fragment key={metric.guid}>
											<Grid item xs={1}></Grid>
											<Grid item xs={7} md={4}>
												<CerebroText>{metric.metricName}</CerebroText>
											</Grid>
											<Grid item xs={4} md={7}>
												<NumberFormat
													value={metric.trailingTwelveMonths}
													displayType="text"
													thousandSeparator={true}
													prefix="$"
												/>
											</Grid>
										</React.Fragment>
									))}
							</Grid>
						)}

						<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
							<Grid item xs={12}>
								{showLinks ? (
									<CerebroLink
										component={Link}
										to={`/${guid}/${Urls.Documents.Root}`}
										style={{
											display: 'flex',
											color: theme.palette.text.primary,
											alignItems: 'center',
											marginBottom: 3,
											fontWeight: 'bold',
										}}
									>
										{getCompleteIndicator(documentsComplete)}
										Documents
									</CerebroLink>
								) : (
									<CerebroText sx={{ fontWeight: 'bold' }}>Documents</CerebroText>
								)}
							</Grid>
							{documentsQuery.data.map(document => (
								<React.Fragment key={document.guid}>
									<Grid item xs={1}></Grid>
									<Grid item xs={11}>
										<CerebroText>
											{getCompleteIndicator(
												document.notApplicable === true || document.completedOn != null
											)}
											{document.applicationTaskTitle}
										</CerebroText>
									</Grid>
								</React.Fragment>
							))}
						</Grid>

						<Grid container spacing={2} sx={{ marginBottom: compressView ? 2 : 6 }}>
							<Grid item xs={12}>
								{showLinks ? (
									<CerebroLink
										component={Link}
										to={`/${guid}/${Urls.DealTypePreference.Root}`}
										style={{
											display: 'flex',
											color: theme.palette.text.primary,
											alignItems: 'center',
											marginBottom: 3,
											fontWeight: 'bold',
										}}
									>
										{getCompleteIndicator(loanOptionsComplete)}
										Loan Options
									</CerebroLink>
								) : (
									<CerebroText sx={{ fontWeight: 'bold' }}>Loan Options</CerebroText>
								)}
							</Grid>
							{!loanOptionsComplete && (
								<>
									<Grid item xs={12}>
										No loan options have been selected.
									</Grid>
								</>
							)}
							{typesSelected.length > 0 && (
								<>
									{typesSelected.map(item => (
										<React.Fragment key={item}>
											<Grid item xs={1}></Grid>
											<Grid item xs={11}>
												{item}
											</Grid>
										</React.Fragment>
									))}
								</>
							)}
							{typesPreferred.length > 0 && typesSelected.length === 0 && (
								<>
									{typesPreferred.map(item => (
										<React.Fragment key={item}>
											<Grid item xs={1}></Grid>
											<Grid item xs={11}>
												{item}
											</Grid>
										</React.Fragment>
									))}
								</>
							)}
						</Grid>
					</>
				)}
		</>
	);
};

const isRequestComplete = (
	allocation: DealRequestAllocation | null | undefined,
	realEstateTotal: number,
	refinanceTotal: number
): boolean => {
	if (!allocation) return false;

	if ((allocation.acquiringBusiness ?? false) === true && (allocation.valueOfferedAmount ?? 0) > 0) return true;
	if ((allocation.otherFinancingNeeds ?? false) === true && (allocation.otherFinancingAmount ?? 0) > 0) return true;
	if ((allocation.purchaseRealEstate ?? false) === true && realEstateTotal > 0) return true;
	if ((allocation.refinanceExistingDebt ?? false) === true && refinanceTotal > 0) return true;

	if ((allocation.purchaseWorkingCapital ?? false) === true && (allocation.totalWorkingCapitalAmount ?? 0) > 0)
		return true;

	if (
		(allocation.financeExpensesOrGrowth ?? false) === true &&
		(allocation.totalGrowthCapitalHardCostAmount ?? 0) + (allocation.totalGrowthCapitalSoftCostAmount ?? 0) > 0
	)
		return true;

	if (
		(allocation.purchaseEquipment ?? false) === true &&
		(allocation.purchaseEquipmentAmount ?? 0) +
			(allocation.purchaseVehiclesAmount ?? 0) +
			(allocation.purchaseHelicopterAmount ?? 0) +
			(allocation.purchaseFixedWingAircraftAmount ?? 0) +
			(allocation.purchaseMarineAmount ?? 0) >
			0
	)
		return true;

	return false;
};

const isCompanySectionComplete = (deal: Deal | null | undefined, isBorrower: boolean): boolean => {
	if (!deal) return false;

	if (isBorrower) {
		if (!deal.companyIndustry && !deal.companyName && !deal.state) {
			return false;
		}
	}

	if (!isBorrower) {
		if (!deal.acquisitionIndustry && !deal.companyName && !deal.acquisitionState) {
			return false;
		}
	}

	return true;
};

const isFinancialSectionComplete = (
	metrics: DealFinancialMetric[] | null | undefined,
	balanceSheet: DealFinancialMetric[] | null | undefined
): boolean => {
	if (!metrics || metrics.length == 0 || !balanceSheet || balanceSheet.length == 0) return false;

	const income =
		metrics
			.filter(
				i =>
					i.financialMetricType === 'AdjustedEbitda' ||
					i.financialMetricType === 'NetIncome' ||
					i.financialMetricType === 'TotalRevenue'
			)
			.reduce((sum, metric) => sum + (metric.trailingTwelveMonths ?? 0), 0) ?? 0;

	const balanceSheetComplete = balanceSheet.filter(i => i.trailingTwelveMonths == null).length === 0;

	return income > 0 && balanceSheetComplete;
};

const isMetricIncomeDisplayType = (metric: DealFinancialMetric): boolean => {
	return (
		metric.financialMetricType === 'TotalRevenue' ||
		metric.financialMetricType === 'AdjustedEbitda' ||
		metric.financialMetricType === 'NetIncome'
	);
};

export interface CerebroSummaryProps {
	showLinks: boolean;
	compressView: boolean;
}
